import { eq, inArray, useLiveQuery } from "@tanstack/react-db";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAppSessionScope } from "~/lib/app-session-scope";
import {
  combineCollectionErrorState,
  resolveCollectionErrorPayload,
} from "~/lib/tanstack-db-collection-errors";
import type { PlatformApiTrpcClient } from "~/lib/trpc/platform-api-adapter";
import {
  getAssetDomainCollections,
  getAssetDomainOverlayQueryOptions,
} from "./collections";
import type {
  AssetOndoProviderExtensionModel,
  AssetProviderExtensionModel,
} from "./models";
import { normalizeAssetDomainAssetIds } from "./models";

function mapRowsByAssetId<T extends { assetId: string }>(
  rows: readonly T[],
  aliasesByAssetId?: ReadonlyMap<string, readonly string[]>,
): Map<string, T> {
  const mapped = new Map<string, T>();

  for (const row of rows) {
    mapped.set(row.assetId, row);
    const aliases = aliasesByAssetId?.get(row.assetId) ?? [];
    for (const alias of aliases) {
      mapped.set(alias, row);
    }
  }

  return mapped;
}

function mapProviderRowsByAssetId(
  rows: readonly AssetProviderExtensionModel[],
  aliasesByAssetId?: ReadonlyMap<string, readonly string[]>,
): Map<string, AssetProviderExtensionModel[]> {
  const mapped = new Map<string, AssetProviderExtensionModel[]>();

  function append(assetId: string, row: AssetProviderExtensionModel) {
    const existing = mapped.get(assetId);
    if (existing) {
      existing.push(row);
      return;
    }

    mapped.set(assetId, [row]);
  }

  for (const row of rows) {
    append(row.assetId, row);
    const aliases = aliasesByAssetId?.get(row.assetId) ?? [];
    for (const alias of aliases) {
      append(alias, row);
    }
  }

  return mapped;
}

function mapOndoRowsByAssetId(
  rows: readonly AssetProviderExtensionModel[],
  aliasesByAssetId?: ReadonlyMap<string, readonly string[]>,
): Map<string, AssetOndoProviderExtensionModel> {
  const mapped = new Map<string, AssetOndoProviderExtensionModel>();

  for (const row of rows) {
    if (row.provider !== "ondo") {
      continue;
    }

    mapped.set(row.assetId, row);
    const aliases = aliasesByAssetId?.get(row.assetId) ?? [];
    for (const alias of aliases) {
      mapped.set(alias, row);
    }
  }

  return mapped;
}

function buildAssetIdAliases(
  assetIds: readonly string[],
): Map<string, readonly string[]> {
  const aliasesByAssetId = new Map<string, string[]>();

  for (const rawAssetId of assetIds) {
    const alias = rawAssetId.trim();
    if (alias.length === 0) {
      continue;
    }

    const normalizedAssetId = normalizeAssetDomainAssetIds([alias])[0];
    if (!normalizedAssetId || normalizedAssetId === alias) {
      continue;
    }

    const aliases = aliasesByAssetId.get(normalizedAssetId);
    if (aliases) {
      aliases.push(alias);
      continue;
    }

    aliasesByAssetId.set(normalizedAssetId, [alias]);
  }

  return aliasesByAssetId;
}

export function useAssetDomainSlice(input: {
  assetIds: readonly string[];
  enabled: boolean;
  hydrateOverlays?: boolean;
  platformApi?: PlatformApiTrpcClient;
}) {
  const sessionScope = useAppSessionScope();
  const normalizedAssetIds = useMemo(
    () => normalizeAssetDomainAssetIds(input.assetIds),
    [input.assetIds],
  );
  const assetIdAliases = useMemo(
    () => buildAssetIdAliases(input.assetIds),
    [input.assetIds],
  );
  const collections = useMemo(
    () =>
      getAssetDomainCollections({ sessionScopeToken: sessionScope.scopeToken }),
    [sessionScope.scopeToken],
  );
  const shouldHydrateOverlays = input.hydrateOverlays ?? true;

  const hydrationQuery = useQuery({
    ...getAssetDomainOverlayQueryOptions({
      assetIds: normalizedAssetIds,
      platformApi: input.platformApi,
      sessionScopeToken: sessionScope.scopeToken,
    }),
    enabled:
      shouldHydrateOverlays && input.enabled && normalizedAssetIds.length > 0,
  });

  const assetQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({ asset: collections.asset })
            .where(({ asset }) => eq(asset.assetId, normalizedAssetIds[0]!))
        : queryBuilder
            .from({ asset: collections.asset })
            .where(({ asset }) => inArray(asset.assetId, normalizedAssetIds));
    },
  });

  const assetPriceQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({ assetPrice: collections.assetPrice })
            .where(({ assetPrice }) =>
              eq(assetPrice.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({ assetPrice: collections.assetPrice })
            .where(({ assetPrice }) =>
              inArray(assetPrice.assetId, normalizedAssetIds),
            );
    },
  });

  const taxonomyQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({ assetTaxonomy: collections.assetTaxonomy })
            .where(({ assetTaxonomy }) =>
              eq(assetTaxonomy.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({ assetTaxonomy: collections.assetTaxonomy })
            .where(({ assetTaxonomy }) =>
              inArray(assetTaxonomy.assetId, normalizedAssetIds),
            );
    },
  });

  const equityQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({ assetEquityDetails: collections.assetEquityDetails })
            .where(({ assetEquityDetails }) =>
              eq(assetEquityDetails.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({ assetEquityDetails: collections.assetEquityDetails })
            .where(({ assetEquityDetails }) =>
              inArray(assetEquityDetails.assetId, normalizedAssetIds),
            );
    },
  });

  const providerExtensionsQuery = useLiveQuery({
    query: (queryBuilder) => {
      if (!input.enabled || normalizedAssetIds.length === 0) {
        return undefined;
      }

      return normalizedAssetIds.length === 1
        ? queryBuilder
            .from({
              assetProviderExtension: collections.assetProviderExtensions,
            })
            .where(({ assetProviderExtension }) =>
              eq(assetProviderExtension.assetId, normalizedAssetIds[0]!),
            )
        : queryBuilder
            .from({
              assetProviderExtension: collections.assetProviderExtensions,
            })
            .where(({ assetProviderExtension }) =>
              inArray(assetProviderExtension.assetId, normalizedAssetIds),
            );
    },
  });

  const assetRows = useMemo(() => assetQuery.data ?? [], [assetQuery.data]);
  const assetPriceRows = useMemo(
    () => assetPriceQuery.data ?? [],
    [assetPriceQuery.data],
  );
  const taxonomyRows = useMemo(
    () => taxonomyQuery.data ?? [],
    [taxonomyQuery.data],
  );
  const equityRows = useMemo(() => equityQuery.data ?? [], [equityQuery.data]);
  const providerExtensionRows = useMemo(
    () => providerExtensionsQuery.data ?? [],
    [providerExtensionsQuery.data],
  );
  const residentAssetIds = useMemo(
    () => new Set(assetRows.map((row) => row.assetId)),
    [assetRows],
  );
  const isOverlayReplayPending =
    hydrationQuery.isFetching &&
    normalizedAssetIds.some((assetId) => !residentAssetIds.has(assetId));
  const collectionError = combineCollectionErrorState(
    input.enabled && normalizedAssetIds.length > 0
      ? [
          collections.asset,
          collections.assetPrice,
          collections.assetTaxonomy,
          collections.assetEquityDetails,
          collections.assetProviderExtensions,
        ]
      : [],
  );

  return {
    assetIds: normalizedAssetIds,
    assetById: useMemo(
      () => mapRowsByAssetId(assetRows, assetIdAliases),
      [assetIdAliases, assetRows],
    ),
    assetPriceByAssetId: useMemo(
      () => mapRowsByAssetId(assetPriceRows, assetIdAliases),
      [assetIdAliases, assetPriceRows],
    ),
    equityDetailsByAssetId: useMemo(
      () => mapRowsByAssetId(equityRows, assetIdAliases),
      [assetIdAliases, equityRows],
    ),
    clearError: collectionError.clearError,
    error: resolveCollectionErrorPayload({
      error: hydrationQuery.error ?? collectionError.lastError,
      fallbackMessage: "Failed to load asset domain data.",
      isError: hydrationQuery.isError || collectionError.isError,
    }),
    errorCount: collectionError.errorCount,
    isError: hydrationQuery.isError || collectionError.isError,
    // Callers displaying supplied accounting values need identity readiness
    // independently of optional, current-market price hydration.
    isIdentityLoading:
      (shouldHydrateOverlays ? isOverlayReplayPending : false) ||
      assetQuery.isLoading ||
      equityQuery.isLoading,
    isLoading:
      (shouldHydrateOverlays ? isOverlayReplayPending : false) ||
      assetQuery.isLoading ||
      assetPriceQuery.isLoading ||
      taxonomyQuery.isLoading ||
      equityQuery.isLoading ||
      providerExtensionsQuery.isLoading,
    ondoProviderExtensionByAssetId: useMemo(
      () => mapOndoRowsByAssetId(providerExtensionRows, assetIdAliases),
      [assetIdAliases, providerExtensionRows],
    ),
    providerExtensionsByAssetId: useMemo(
      () => mapProviderRowsByAssetId(providerExtensionRows, assetIdAliases),
      [assetIdAliases, providerExtensionRows],
    ),
    status: shouldHydrateOverlays ? hydrationQuery.status : "success",
    taxonomyByAssetId: useMemo(
      () => mapRowsByAssetId(taxonomyRows, assetIdAliases),
      [assetIdAliases, taxonomyRows],
    ),
  };
}
